import { useCallback, useEffect, useRef } from "react";
import { CaseAppealListRow } from "../../../data-model/caseAppealsTable";
import { makePostCallForOncologySubmission } from "../apis/makePostCallForOncologySubmission";
import { CaseAppealsTableState } from "./useCaseAppealsTableData";
import { AppealListFilterPayload } from "./useFilterPayload";

/**
 * Refetches appeal list data when the filter payload changes after initial load.
 *
 * @param filterPayload - Current appeal list request payload
 * @param initialDataFetchingExecuted - Whether the first page has loaded
 * @param setTableData - Raw API rows setter
 * @param setCaseAppealsState - Table loading/error/data state setter
 * @param setPaginationResetCount - Resets client pagination when filters change
 */
export const useFilterAndSearchEffect = (
  filterPayload: AppealListFilterPayload | null,
  initialDataFetchingExecuted: boolean,
  setTableData: React.Dispatch<React.SetStateAction<CaseAppealListRow[]>>,
  setCaseAppealsState: React.Dispatch<
    React.SetStateAction<CaseAppealsTableState>
  >,
  setPaginationResetCount: React.Dispatch<React.SetStateAction<number>>,
): void => {
  const initialDataFetchingExecutedRef = useRef(initialDataFetchingExecuted);
  /**
   * The first payload emitted after an initial load carries the same filters
   * that load already fetched, so it is skipped to avoid a duplicate page-1
   * request on every mount and Refresh.
   */
  const skipNextPayloadRef = useRef(true);
  const setTableDataRef = useRef(setTableData);
  const setCaseAppealsStateRef = useRef(setCaseAppealsState);
  const setPaginationResetCountRef = useRef(setPaginationResetCount);

  useEffect(() => {
    initialDataFetchingExecutedRef.current = initialDataFetchingExecuted;
    setTableDataRef.current = setTableData;
    setCaseAppealsStateRef.current = setCaseAppealsState;
    setPaginationResetCountRef.current = setPaginationResetCount;
  }, [
    initialDataFetchingExecuted,
    setTableData,
    setCaseAppealsState,
    setPaginationResetCount,
  ]);

  const fetchData = useCallback(() => {
    if (filterPayload) {
      makePostCallForOncologySubmission(
        filterPayload,
        setCaseAppealsStateRef.current,
        setTableDataRef.current,
        setPaginationResetCountRef.current,
      );
    }
  }, [filterPayload]);

  useEffect(() => {
    // Refresh flips the flag back to false; the initial fetch runs again and
    // will once more re-emit a payload this hook must not act on.
    if (!initialDataFetchingExecuted) {
      skipNextPayloadRef.current = true;
    }
  }, [initialDataFetchingExecuted]);

  useEffect(() => {
    if (!filterPayload || !initialDataFetchingExecutedRef.current) {
      return;
    }

    if (skipNextPayloadRef.current) {
      skipNextPayloadRef.current = false;
      return;
    }

    fetchData();
  }, [filterPayload, fetchData]);
};
